import { type BehaviorSubject } from 'rxjs';
import { type ILogObj, type Logger } from 'tslog';
import { type BDCollabAwareness } from '../awareness/collab-awareness.types';
import type * as Y from 'yjs';

// configuration set in environment and provided via injection token
export type CollabServerConfig = {
  proxyUrls?: string[]; // urls for stun/turn servers
  proxyUsername?: string;
  proxyCredential?: string;
  signalingServers: string[];
};

export type BDCollabIceServerConfig = {
  urls: string[];
  username?: string;
  credential?: string;
};

export type BDCollabServerConfig = {
  signalingUrls: string[];
  iceServers?: BDCollabIceServerConfig[];
  params?: Record<string, string>;
  authParamKey?: string;
  refreshAuthToken?: () => Promise<string>;
};

export type NetworkState = 'connected' | 'connecting' | 'disconnected';

/**
 * Base interface for defining Yjs provider.
 */
export interface BDCollabProvider {
  doc: Y.Doc;
  roomName?: string;
  awareness: BDCollabAwareness;
  connect(): void;
  disconnect(): void;
  destroy(): void;
}

/**
 * Base interface for defining the schema of a collaborative Yjs document.
 *
 * Serves as a blueprint for the structure of shared data that you can get from the provider.
 */
export interface BDCollabSchema {
  maps: Record<string, unknown>;
  arrays: Record<string, unknown>;
}

/**
 * Abstract base class for managing Yjs providers.
 *
 * Provides a common interface for integrating different Yjs providers(such as WebSocket or WebRTC)
 * while keeping the implementation decoupled from the syncing logic.
 *
 * @template Provider - The Yjs provider type (e.g., WebsocketProvider, WebrtcProvider).
 * @template Schema - The schema type representing the shared document structure.
 */
export abstract class BDCollabProviderBase<
  Provider extends BDCollabProvider,
  Schema extends BDCollabSchema,
  Config extends object,
> implements BDCollabProvider
{
  protected provider: Provider;
  public awareness: BDCollabAwareness;

  constructor(
    public doc: Y.Doc,
    public roomName: string | undefined,
    protected serverConfig: Config,
    protected logger: Logger<ILogObj> | null = null,
  ) {
    this.provider = this.initializeProvider();
    this.awareness = this.provider.awareness;
  }

  getMap<K extends keyof Schema['maps']>(key: K): Y.Map<Schema['maps'][K]> {
    return this.doc.getMap<Schema['maps'][K]>(key as string);
  }

  getArray<K extends keyof Schema['arrays']>(
    key: K,
  ): Y.Array<Schema['arrays'][K]> {
    return this.doc.getArray<Schema['arrays'][K]>(key as string);
  }

  connect(): void {
    this.provider.connect();
  }

  disconnect(): void {
    this.provider.disconnect();
  }

  destroy(): void {
    this.provider.destroy();
  }

  abstract getNetworkState(): BehaviorSubject<NetworkState>;

  protected abstract initializeProvider(): Provider;
}
